import { db, SyncQueueItem } from '@/shared/lib/db'
import { firestore } from '@/shared/lib/firebase'
import { deleteDoc, doc, setDoc } from 'firebase/firestore'

export class SyncService {
    private isSyncing = false
    private userId: string | null = null

    setUserId(uid: string | null) {
        this.userId = uid
    }

    async processQueue() {
        if (this.isSyncing || !this.userId) return
        this.isSyncing = true

        try {
            // Get all pending items and failed items that explicitly need retry
            // For simplicity, we'll fetch both. In a real app, query optimization is needed.
            const pendingItems = await db.sync_queue.where('status').equals('PENDING').toArray()
            const failedItems = await db.sync_queue.where('status').equals('FAILED').toArray()
            
            const itemsToSync = [...pendingItems, ...failedItems].sort((a, b) => a.created_at.getTime() - b.created_at.getTime())

            for (const item of itemsToSync) {
                if (item.status === 'FAILED' && item.retryCount > 5) {
                    console.log(`Skipping item ${item.id} due to max retries`)
                    continue
                }
                await this.processItem(item)
            }
        } catch (error) {
            console.error('Error processing sync queue:', error)
        } finally {
            this.isSyncing = false
        }
    }

    private async processItem(item: SyncQueueItem & { id: string }) {
        try {
            await this.syncToFirestore(item)
            
            // On success, delete from queue
            await db.sync_queue.delete(item.id)
            console.log(`Synced item ${item.id} (${item.operation} ${item.entityTable})`)
        } catch (error) {
            console.error(`Failed to sync item ${item.id}:`, error)
            await db.sync_queue.update(item.id, {
                status: 'FAILED', 
                retryCount: (item.retryCount || 0) + 1,
                error: (error as any).message,
                updated_at: new Date()
            })
        }
    }

    private async syncToFirestore(item: SyncQueueItem) {
        if (!this.userId) throw new Error("User ID not set for SyncService")

        const payload = item.payload
        if (!payload && item.operation !== 'DELETE') {
            throw new Error("Payload missing for non-DELETE operation")
        }

        let path = ''
        const userId = this.userId
        
        switch (item.entityTable) {
            case 'project':
                path = `users/${userId}/projects/${item.entityId}`
                break
            case 'storyboards':
                // Storyboards are subcollections of projects: users/{uid}/projects/{pid}/storyboards/{sid}
                // We need projectId from payload or somewhere.
                // If DELETE, we might not have payload... which is a problem for path generation if generic.
                // For now, assuming payload exists or we can derive it.
                // Ideally, SyncQueueItem should store 'parentId' or similar metadata.
                const projectId = payload?.projectId
                 if (!projectId) {
                    // Fallback or Error? 
                    // If DELETE, we can't find path without projectId.
                    throw new Error("Cannot sync storyboard without projectId")
                 }
                path = `users/${userId}/projects/${projectId}/storyboards/${item.entityId}` 
                break
            case 'shotlists':
                 // users/{userId}/projects/{projectId}/shotlists/{shotListId}
                 const slProjectId = payload?.projectId
                 if (!slProjectId) throw new Error("Cannot sync shotlist without projectId")
                 path = `users/${userId}/projects/${slProjectId}/shotlists/${item.entityId}`
                 break
            case 'story': // Legacy: handle old items queued with singular name
            case 'stories':
                 const stProjectId2 = payload?.projectId
                 if (!stProjectId2) throw new Error("Cannot sync story without projectId")
                 path = `users/${userId}/projects/${stProjectId2}/stories/${item.entityId}`
                 break
            case 'screenplays':
                 const spProjectId = payload?.projectId
                 if (!spProjectId) throw new Error("Cannot sync screenplay without projectId")
                 path = `users/${userId}/projects/${spProjectId}/screenplays/${item.entityId}`
                 break
            default:
                console.warn(`Unknown entity table: ${item.entityTable}`)
                return
        }

        if (!path) {
             throw new Error(`Invalid path generated for ${item.entityTable}`)
        }

        const docRef = doc(firestore, path)

        if (item.operation === 'DELETE') {
            await deleteDoc(docRef)
        } else {
            // MERGE true to avoid overwriting unrelated fields if any
            await setDoc(docRef, payload, { merge: true })
        }
    }
}

export const syncService = new SyncService()
